import { isLocal } from './env';

export function downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function downloadCsv(content: string, filename: string) {
    return downloadText(content, 'text/csv', filename);
}

export function downloadText(content: string, type: string, filename: string) {
    const blob = new Blob([content], { type });
    downloadBlob(blob, filename);
}

const EXTENSION_TYPES: Record<string, string> = {
    'audio/wav': '.wav',
    'audio/ogg': '.ogg',
    'audio/x-wav': '.wav',
    'audio/flac': '.flac',
    'audio/x-flac': '.flac',
};

export function mimeTypeToExtension(type: string): string {
    return EXTENSION_TYPES[type] || '';
}

export function getFileNameFromUrl(fileUrl: string): string {
    try {
        return new URL(fileUrl).pathname.split('/').pop() || 'file';
    } catch {
        return 'file';
    }
}

export function isPlatformFile(fileUrl: string): boolean {
    try {
        const url = new URL(fileUrl);
        if (isLocal() && url.hostname === 'localhost') {
            return true;
        }

        return url.hostname.startsWith('remotely-platform'); // e.g., https://remotely-platform.s3.eu-central-1.wasabisys.com/resume/d049f356-e913-49d3-961f-e47cbf54d35f.pdf
    } catch {
        return false;
    }
}

export function getPublicDocumentUrl(
    kind: 'resume' | 'assessment' | 'certification',
    publicId: string | undefined | null,
    filename: string,
    fallbackUrl: string,
): string {
    if (!publicId || !filename) {
        return fallbackUrl;
    }
    if (kind === 'certification' && !isPlatformFile(fallbackUrl)) {
        // Certification evidence can be a user-entered external link rather than
        // an uploaded file. Only proxy files actually stored on the platform;
        // link straight to external URLs instead of routing them through the server.
        return fallbackUrl;
    }
    const path = `/public/documents/${kind}/${encodeURIComponent(publicId)}/${encodeURIComponent(filename)}`;
    if (kind === 'certification') {
        // Certifications are a list, so the specific evidence URL to proxy is
        // passed as a query param instead of being looked up by a single field.
        return `${path}?url=${encodeURIComponent(fallbackUrl)}`;
    }
    return path;
}

export function getCandidateResumeFilename(candidateName: string, resumeUrl: string): string {
    const name = candidateName.trim();
    const fallback = getFileNameFromUrl(resumeUrl);
    const ext = fallback.includes('.') ? `.${fallback.split('.').pop()}` : '';
    if (name) {
        return `${name}${ext || '.pdf'}`;
    }
    return fallback;
}

export function getCertificationEvidenceFilename(
    organization: string,
    certificationName: string,
    candidateName: string,
    evidenceUrl: string,
): string {
    const fallback = getFileNameFromUrl(evidenceUrl);
    const ext = fallback.includes('.') ? `.${fallback.split('.').pop()}` : '.pdf';
    const label = [organization, certificationName, candidateName].filter(Boolean).join(' - ');
    return label ? `${label}${ext}` : fallback;
}
